import React, { useState, Children } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { OrderModal } from '../components/OrderModal';
import { ArrowLeft, CheckCircle2, ArrowRight } from 'lucide-react';
import { getContent } from '../utils/contentStore';
import { getSiteConfig } from '../utils/siteConfig';
import { useLanguage } from '../utils/i18n';
import { useSEO } from '../utils/useSEO';
export function ProductPage() {
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const [orderModalState, setOrderModalState] = useState({
    isOpen: false,
    productName: ''
  });
  const allProducts = getContent().products;
  const products = allProducts.filter(p => p.active !== false);
  const showPrices = getSiteConfig().catalog.showPrices;
  const {
    t,
    loc
  } = useLanguage();
  useSEO('catalog');
  const product = allProducts.find(p => p.id === productId);
  if (!product) {
    return <Navigate to="/catalog" replace data-id="element-1113" />;
  }
  // Dynamic line detection: find active children with parentLine === productId
  const activeChildren = products.filter(p => p.parentLine === productId);
  const isLine = activeChildren.length > 0;
  const variants = activeChildren;
  const openOrderModal = (name: string) => {
    setOrderModalState({
      isOpen: true,
      productName: name
    });
  };
  const closeOrderModal = () => {
    setOrderModalState(prev => ({
      ...prev,
      isOpen: false
    }));
  };
  return <div className="bg-gray-50 min-h-screen flex flex-col" data-id="element-1114">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 md:px-6 max-w-7xl pt-24 pb-4" data-id="element-1115">
        <Link to="/catalog" className="inline-flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors text-sm font-medium" data-id="element-1116">
          <ArrowLeft className="h-4 w-4" data-id="element-1117" />
          {t('product_page_back')}
        </Link>
      </div>

      {isLine ? (
    /* =========================================
    LINE MODE (velora, exterra)
    ========================================= */
    <>
          {/* Hero Section (Line) */}
          <div className="container mx-auto px-4 md:px-6 max-w-7xl py-6" data-id="element-1118">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-1119">
              {/* Product Image */}
              <div className="relative" data-id="element-1120">
                <div className="aspect-square rounded-lg overflow-hidden bg-white shadow-lg" data-id="element-1121">
                  <img src={product.image} alt={loc(product.name)} className="w-full h-full object-cover object-center" data-id="element-1122" />
                </div>
                <div className="absolute top-6 left-6 bg-gray-900 text-white px-4 py-2 text-sm font-bold rounded-sm uppercase tracking-wider" data-id="element-1123">
                  {loc(product.category)}
                </div>
              </div>

              {/* Product Info */}
              <div className="flex flex-col" data-id="element-1124">
                <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4 uppercase leading-tight" data-id="element-1125">
                  {loc(product.name)}
                </h1>
                <p className="text-xl text-gray-700 font-medium mb-6" data-id="element-1126">
                  {loc(product.subtitle)}
                </p>

                {/* Full Description in Hero for Lines */}
                <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line mb-8" data-id="element-1127">
                  {loc(product.description)}
                </p>

                {/* Key Advantages */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100" data-id="element-1128">
                  <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2 uppercase" data-id="element-1129">
                    <CheckCircle2 className="h-5 w-5 text-green-600" data-id="element-1130" />
                    {t('product_page_advantages')}
                  </h3>
                  <ul className="space-y-3" data-id="element-1131">
                    {product.advantages.map((advantage, index) => <li key={index} className="flex items-start gap-3" data-id="element-1132">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1133" />
                        <span className="text-gray-700" data-id="element-1134">{loc(advantage)}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Specifications (Line) — only show if specs exist */}
          {product.specs.length > 0 && <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8" data-id="element-1135">
              <div className="max-w-4xl" data-id="element-1136">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-6 uppercase" data-id="element-1137">
                  {t('product_page_specs')}
                </h2>
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1138">
                  <div className="divide-y divide-gray-100" data-id="element-1139">
                    {product.specs.map((spec, index) => <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 sm:p-6 hover:bg-gray-50 transition-colors" data-id="element-1140">
                        <div className="font-medium text-gray-900" data-id="element-1141">
                          {loc(spec.label)}
                        </div>
                        <div className="text-gray-700" data-id="element-1142">{loc(spec.value)}</div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>}

          {/* Variants Section (Line) */}
          <div className="bg-white py-12 border-t border-gray-200 mt-4 flex-1" data-id="element-1143">
            <div className="container mx-auto px-4 md:px-6 max-w-7xl" data-id="element-1144">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-10 uppercase text-center" data-id="element-1145">
                {t('product_line_variants_title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1146">
                {variants.map(variant => <div key={variant.id} className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col" data-id="element-1147">
                    {/* Variant Image */}
                    <div className="aspect-[4/3] overflow-hidden bg-gray-100 relative group" data-id="element-1148">
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors z-10" data-id="element-1149" />
                      <img src={variant.image} alt={loc(variant.name)} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" data-id="element-1150" />
                    </div>

                    {/* Variant Info */}
                    <div className="p-6 flex-1 flex flex-col" data-id="element-1151">
                      <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2 uppercase" data-id="element-1152">
                        {loc(variant.name)}
                      </h3>
                      <p className="text-gray-600 text-sm mb-6 flex-1" data-id="element-1153">
                        {loc(variant.subtitle)}
                      </p>

                      {showPrices && <div className="flex items-baseline gap-2 mb-6" data-id="element-1154">
                          <span className="text-sm text-gray-500 uppercase tracking-wide" data-id="element-1155">
                            {t('product_line_from')}
                          </span>
                          <span className="text-3xl font-bold text-gray-900" data-id="element-1156">
                            {variant.price} с
                          </span>
                          <span className="text-gray-500 text-sm" data-id="element-1157">
                            / {loc(variant.unit)}
                          </span>
                        </div>}

                      {/* Variant Actions */}
                      <div className="grid grid-cols-2 gap-3 mt-auto" data-id="element-1158">
                        <Link to={`/catalog/${variant.id}`} className="inline-flex items-center justify-center rounded-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 uppercase tracking-wide border-2 border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500 px-3 py-1.5 text-xs text-center w-full" data-id="element-1159">
                          {t('product_line_details')}
                        </Link>
                        <Button size="sm" variant="primary" onClick={() => openOrderModal(loc(variant.name))} className="w-full" data-id="element-1160">
                          {t('product_line_order_variant')}
                        </Button>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </>) : (
    /* =========================================
    INDIVIDUAL PRODUCT MODE
    ========================================= */
    <>
          {/* Hero Section (Individual) */}
          <div className="container mx-auto px-4 md:px-6 max-w-7xl py-6" data-id="element-1161">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-1162">
              {/* Product Image */}
              <div className="relative" data-id="element-1163">
                <div className="aspect-square rounded-lg overflow-hidden bg-white shadow-lg" data-id="element-1164">
                  <img src={product.image} alt={loc(product.name)} className="w-full h-full object-cover object-center" data-id="element-1165" />
                </div>
                <div className="absolute top-6 left-6 bg-gray-900 text-white px-4 py-2 text-sm font-bold rounded-sm uppercase tracking-wider" data-id="element-1166">
                  {loc(product.category)}
                </div>
              </div>

              {/* Product Info */}
              <div className="flex flex-col" data-id="element-1167">
                <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4 uppercase leading-tight" data-id="element-1168">
                  {loc(product.name)}
                </h1>
                <p className="text-xl text-gray-700 font-medium mb-6" data-id="element-1169">
                  {loc(product.subtitle)}
                </p>

                {showPrices && <div className="flex items-baseline gap-3 mb-8" data-id="element-1170">
                    <span className="text-sm text-gray-500 uppercase tracking-wide" data-id="element-1171">
                      {t('product_page_price_from')}
                    </span>
                    <span className="text-5xl font-bold text-gray-900" data-id="element-1172">
                      {product.price} с
                    </span>
                    <span className="text-gray-500 text-lg" data-id="element-1173">
                      / {loc(product.unit)}
                    </span>
                  </div>}

                <Button size="lg" onClick={() => openOrderModal(loc(product.name))} className="w-full sm:w-auto mb-8" data-id="element-1174">
                  {t('product_page_order')} {loc(product.name)}
                </Button>

                {/* Key Advantages */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100" data-id="element-1175">
                  <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2 uppercase" data-id="element-1176">
                    <CheckCircle2 className="h-5 w-5 text-green-600" data-id="element-1177" />
                    {t('product_page_advantages')}
                  </h3>
                  <ul className="space-y-3" data-id="element-1178">
                    {product.advantages.map((advantage, index) => <li key={index} className="flex items-start gap-3" data-id="element-1179">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1180" />
                        <span className="text-gray-700" data-id="element-1181">{loc(advantage)}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Description Section (Individual) */}
          <div className="bg-white py-8 border-y border-gray-200 mt-4" data-id="element-1182">
            <div className="container mx-auto px-4 md:px-6 max-w-7xl" data-id="element-1183">
              <div className="max-w-4xl" data-id="element-1184">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-6 uppercase" data-id="element-1185">
                  {t('product_page_description')}
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line" data-id="element-1186">
                  {loc(product.description)}
                </p>
              </div>
            </div>
          </div>

          {/* Technical Specifications (Individual) — only show if specs exist */}
          {product.specs.length > 0 && <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8" data-id="element-1187">
              <div className="max-w-4xl" data-id="element-1188">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-6 uppercase" data-id="element-1189">
                  {t('product_page_specs')}
                </h2>
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-1190">
                  <div className="divide-y divide-gray-100" data-id="element-1191">
                    {product.specs.map((spec, index) => <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 sm:p-6 hover:bg-gray-50 transition-colors" data-id="element-1192">
                        <div className="font-medium text-gray-900" data-id="element-1193">
                          {loc(spec.label)}
                        </div>
                        <div className="text-gray-700" data-id="element-1194">{loc(spec.value)}</div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>}

          {/* CTA Section (Individual) */}
          <div className="bg-gray-900 text-white py-10 mt-auto" data-id="element-1195">
            <div className="container mx-auto px-4 md:px-6 max-w-7xl" data-id="element-1196">
              <div className="text-center max-w-2xl mx-auto" data-id="element-1197">
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 uppercase" data-id="element-1198">
                  {t('product_page_order')} {loc(product.name)}?
                </h2>
                <p className="text-gray-300 mb-8 text-lg" data-id="element-1199">
                  {t('contact_form_success_desc')}
                </p>
                <Button size="lg" variant="primary" onClick={() => openOrderModal(loc(product.name))} data-id="element-1200">
                  {t('product_page_order')}
                </Button>
              </div>
            </div>
          </div>
        </>)}

      <OrderModal isOpen={orderModalState.isOpen} onClose={closeOrderModal} productName={orderModalState.productName} data-id="element-1201" />
    </div>;
}